import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./FeaturesPage.module.css";
import Navigation from "../Home/Navigation";
import { SEO } from "../SEO";
import seoConfig from "../SEO/seoConfig";
import { useInlineTranslation } from "../../services/translationService";

const appFeatures = [
  {
    name: "AI Script Generator",
    icon: "🎬",
    description: "Create complete scripts and auto-convert to optimized video prompts",
    features: [
      "AI-powered script generation for sermons and content",
      "Automatic script-to-prompt conversion",
      "Christian context and biblical accuracy",
      "Multiple script formats (sermon, devotional, teaching)",
      "Structured talking points generation",
      "Scripture integration and references",
      "Content flow optimization",
      "Time-based script structuring",
      "Export in multiple formats"
    ],
    models: ["GPT", "Claude Sonnet"]
  },
  {
    name: "AI Video Caption Generator",
    icon: "📝",
    description: "Transform your videos with AI-powered captions using advanced speech-to-text technology",
    features: [
      "AI-powered speech-to-text transcription using OpenAI Whisper",
      "Real-time caption preview overlay on video",
      "Professional caption styling (15+ styles)",
      "Timeline editing with precise timing controls",
      "Drag-and-drop caption positioning",
      "Multiple export formats (SRT, VTT, MP4 with burned-in captions)",
      "Batch processing for multiple videos",
      "Custom fonts and animations",
      "Auto-sync with video timeline"
    ],
    models: ["OpenAI GPT", "Whisper API"]
  },
  {
    name: "AI Thumbnail Creator",
    icon: "🖼️",
    description: "Create stunning thumbnails with AI-powered image generation and professional design tools",
    features: [
      "AI-powered thumbnail generation from text prompts",
      "Professional drag-and-drop editor",
      "Multi-layer editing (images, text, shapes, assets)",
      "Visual font library (15+ professional fonts)",
      "Custom asset upload and management",
      "Background removal and replacement",
      "Text templates with preset styles",
      "4K export resolution",
      "A/B testing thumbnail variations"
    ],
    models: ["SDXL via Modal", "Claude for optimization"]
  },
  {
    name: "AI Audio Generator",
    icon: "🎵",
    description: "Generate high-quality narrations and melodies with advanced AI voice synthesis",
    features: [
      "AI narration generation with multiple voice options",
      "AI melody and background music creation",
      "Audio editing and mixing tools",
      "Waveform visualization and editing",
      "Voice cloning capabilities",
      "Christian-themed background music library",
      "Audio effects and filters",
      "Multiple format exports (MP3, WAV, AAC)",
      "Commercial use licensing included"
    ],
    models: ["Bark via Modal", "Custom music models"]
  },
  {
    name: "AI Sound Generator",
    icon: "🔊",
    description: "Create professional sound effects and ambient audio for your content",
    features: [
      "AI-generated sound effects for videos",
      "Ambient background audio creation",
      "Custom sound library for Christian content",
      "Audio layering and mixing tools",
      "Real-time sound generation",
      "Multiple audio format support",
      "Royalty-free sound creation",
      "Sync with video timeline",
      "Professional audio quality"
    ],
    models: ["Stable Audio", "Custom sound models"]
  },
  {
    name: "AI Title & Description Generator",
    icon: "✍️",
    description: "Create engaging titles and SEO-optimized descriptions that drive more views",
    features: [
      "AI-powered title generation with Christian context",
      "SEO-optimized descriptions for maximum reach",
      "Platform-specific formatting (YouTube, Instagram, Facebook, TikTok)",
      "Hashtag generation and optimization",
      "A/B testing title suggestions",
      "Engagement prediction scoring",
      "Content calendar integration",
      "Trending topic incorporation",
      "Bulk generation capabilities"
    ],
    models: ["OpenAI GPT", "Claude Sonnet"]
  },
  {
    name: "AI Prompt Generator",
    icon: "🎯",
    description: "Never run out of content ideas with our intelligent Christian content assistant",
    features: [
      "Interactive AI assistant for content ideation",
      "Christian content specialization with Bible knowledge",
      "Trending topic suggestions",
      "Content calendar planning",
      "Scripture-based content prompts",
      "Seasonal and holiday content ideas",
      "Audience engagement optimization",
      "Cross-platform content adaptation",
      "Content series planning"
    ],
    models: ["Claude Sonnet", "GPT for research"]
  },
  {
    name: "AI Image Analysis & Description",
    icon: "👁️",
    description: "Understand and describe your images with advanced computer vision",
    features: [
      "AI-powered image description using advanced vision models",
      "Content tagging and categorization",
      "Accessibility text generation (alt-text)",
      "Batch image processing",
      "Object detection and identification", 
      "Scene analysis and context understanding",
      "Text extraction from images (OCR)",
      "Content moderation and appropriateness checking"
    ],
    models: ["Llama 3.2 Vision", "Google Gemini Vision"]
  },

  {
    name: "AI Content Idea Generator",
    icon: "💡",
    description: "Generate endless Christian content ideas tailored to your audience",
    features: [
      "Christian news content integration",
      "Bible verse suggestions with context",
      "Devotional content generation",
      "Prayer request and response ideas",
      "Seasonal Christian content calendar",
      "Scripture-based content themes",
      "Community engagement prompts",
      "Cross-reference biblical connections"
    ],
    models: ["Claude Sonnet", "GPT with Bible training"]
  },
  {
    name: "Social Media Direct Sharing",
    icon: "📤",
    description: "Upload and share your content directly to social platforms without leaving our app (Creator Lite and above)",
    features: [
      "Direct YouTube upload without YouTube Studio",
      "Automatic video optimization for each platform",
      "Bulk upload and scheduling capabilities (Creator Pro+)",
      "Cross-platform posting (YouTube, Instagram, Facebook, TikTok)",
      "Auto-generated descriptions and tags per platform",
      "Thumbnail optimization for each social network",
      "Publishing calendar and scheduling (Creator Pro+)",
      "Performance analytics and insights",
      "One-click publishing workflow"
    ],
    models: ["YouTube Data API", "Platform-specific optimization AI"]
  },
  {
    name: "Project Management & Collaboration Hub",
    icon: "👥",
    description: "Collaborate seamlessly with your team on all content projects",
    features: [
      "Multi-user project sharing",
      "Version control and history",
      "Comment and approval workflows",
      "Team member role management",
      "Progress tracking and deadlines",
      "Asset library sharing",
      "Workflow automation",
      "Analytics and reporting dashboard"
    ],
    models: ["Platform integration", "AI-powered insights"]
  }
];

const platformAdvantages = [
  {
    title: "Direct Social Media Publishing",
    icon: "📤",
    description: "Upload directly to YouTube and other platforms without switching apps",
    benefits: [
      "Skip YouTube Studio - upload directly from our platform",
      "Auto-optimized descriptions and tags for each platform",
      "Bulk upload and scheduling capabilities",
      "Cross-platform publishing in one click",
      "Built-in analytics and performance tracking"
    ]
  },
  {
    title: "All-in-One Platform",
    icon: "🎪",
    description: "Create, edit, and share all your Christian content from one powerful platform",
    benefits: [
      "No more jumping between 5-8 different tools",
      "Seamless workflow from ideation to publication",
      "Integrated asset management across all projects",
      "Unified brand consistency across all content"
    ]
  },
  {
    title: "Best AI Models at Your Fingertips",
    icon: "🧠",
    description: "Access the most advanced AI models without managing multiple subscriptions",
    benefits: [
      "OpenAI GPT & Whisper for text and transcription",
      "Claude Sonnet for creative content generation",
      "Google Gemini for advanced vision tasks",
      "SDXL for professional image generation",
      "Custom models trained specifically for Christian content"
    ]
  },
  {
    title: "Christian-Focused AI Training",
    icon: "✝️",
    description: "AI models specially trained to understand Christian context and values",
    benefits: [
      "Biblical terminology and theological accuracy",
      "Appropriate tone for different Christian audiences",
      "Cultural sensitivity and doctrinal awareness",
      "Scripture-based content suggestions",
      "Ministry-appropriate language and themes"
    ]
  },
  {
    title: "Complete Workflow Integration",
    icon: "📱",
    description: "From creation to publication - never leave our platform",
    benefits: [
      "Direct publishing to YouTube without YouTube Studio",
      "Instagram, Facebook, and TikTok integration",
      "Platform-specific optimization and formatting",
      "Scheduled posting and content calendar",
      "Unified analytics across all platforms"
    ]
  },
  {
    title: "Massive Cost Savings",
    icon: "💰",
    description: "Save hundreds of dollars monthly vs buying tools separately",
    benefits: [
      "Replace 8+ expensive tools with one platform",
      "No more $150-200/month in subscriptions",
      "Fair use pricing with credit add-ons",
      "Scale up or down based on your needs",
      "Transparent pricing with no hidden fees"
    ]
  },
  {
    title: "Team Collaboration Built-In",
    icon: "🤝",
    description: "Work together seamlessly with your ministry team",
    benefits: [
      "Multi-user access and permissions",
      "Comment and approval workflows", 
      "Shared asset libraries and templates",
      "Progress tracking and project management",
      "Role-based access control"
    ]
  }
];

const competitorComparison = [
  { tool: "Jasper AI (Scripts)", price: "$49/month", limitation: "Generic scripts, no Christian context or video integration" },
  { tool: "Canva Pro", price: "$15/month", limitation: "Design only, no video or AI audio" },
  { tool: "Descript", price: "$15/month", limitation: "Video editing only, no thumbnails or content generation" },
  { tool: "ElevenLabs", price: "$22/month", limitation: "Audio only, no video or design tools" },
  { tool: "Rev.com", price: "$25/hour", limitation: "Transcription only, expensive per-hour pricing" },
  { tool: "Scriptwriting Services", price: "$50-100/project", limitation: "Expensive per-project, no Christian focus" },
  { tool: "Adobe Creative", price: "$60/month", limitation: "No AI generation, steep learning curve" },
  { tool: "Multiple Tools Total", price: "$200-250/month", limitation: "Fragmented workflow, multiple logins, no integration" }
];

export default function FeaturesPage() {
  const [selectedApp, setSelectedApp] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<'apps' | 'advantages' | 'comparison'>('apps');
  const navigate = useNavigate();

  // All app translations at top level (hooks must be called consistently)
  const appTranslations: Record<string, { name: string, description: string, features?: string[] }> = {
    "AI Script Generator": {
      name: useInlineTranslation("AI Script Generator", "Générateur de Scripts IA"),
      description: useInlineTranslation("Create complete scripts and auto-convert to optimized video prompts", "Créez des scripts complets et convertissez-les automatiquement en prompts vidéo optimisés"),
      features: [
        useInlineTranslation("AI-powered script generation for sermons and content", "Génération de scripts IA pour sermons et contenu"),
        useInlineTranslation("Automatic script-to-prompt conversion", "Conversion automatique script vers prompt"),
        useInlineTranslation("Christian context and biblical accuracy", "Contexte chrétien et précision biblique"),
        useInlineTranslation("Multiple script formats (sermon, devotional, teaching)", "Formats de scripts multiples (sermon, dévotion, enseignement)"),
        useInlineTranslation("Structured talking points generation", "Génération de points de discussion structurés"),
        useInlineTranslation("Scripture integration and references", "Intégration et références bibliques"),
        useInlineTranslation("Content flow optimization", "Optimisation du flux de contenu"),
        useInlineTranslation("Time-based script structuring", "Structuration de script basée sur le temps"),
        useInlineTranslation("Export in multiple formats", "Export en formats multiples")
      ]
    },
    "AI Video Caption Generator": {
      name: useInlineTranslation("AI Video Caption Generator", "Générateur de Sous-titres Vidéo IA"),
      description: useInlineTranslation("Transform your videos with AI-powered captions using advanced speech-to-text technology", "Transformez vos vidéos avec des sous-titres IA utilisant la technologie avancée de reconnaissance vocale"),
      features: [
        useInlineTranslation("AI-powered speech-to-text transcription using OpenAI Whisper", "Transcription vocale IA avec OpenAI Whisper"),
        useInlineTranslation("Real-time caption preview overlay on video", "Aperçu en temps réel des sous-titres sur vidéo"),
        useInlineTranslation("Professional caption styling (15+ styles)", "Styles de sous-titres professionnels (15+ styles)"),
        useInlineTranslation("Timeline editing with precise timing controls", "Édition de timeline avec contrôles de timing précis"),
        useInlineTranslation("Drag-and-drop caption positioning", "Positionnement des sous-titres par glisser-déposer"),
        useInlineTranslation("Multiple export formats (SRT, VTT, MP4 with burned-in captions)", "Formats d'export multiples (SRT, VTT, MP4 avec sous-titres incrustés)"),
        useInlineTranslation("Batch processing for multiple videos", "Traitement par lots pour vidéos multiples"),
        useInlineTranslation("Custom fonts and animations", "Polices et animations personnalisées"),
        useInlineTranslation("Auto-sync with video timeline", "Synchronisation auto avec timeline vidéo")
      ]
    },
    "AI Thumbnail Creator": {
      name: useInlineTranslation("AI Thumbnail Creator", "Créateur de Miniatures IA"),
      description: useInlineTranslation("Create stunning thumbnails with AI-powered image generation and professional design tools", "Créez des miniatures époustouflantes avec génération d'images IA et outils de design professionnels"),
      features: [
        useInlineTranslation("AI-powered thumbnail generation from text prompts", "Génération de miniatures IA depuis prompts texte"),
        useInlineTranslation("Professional drag-and-drop editor", "Éditeur glisser-déposer professionnel"),
        useInlineTranslation("Multi-layer editing (images, text, shapes, assets)", "Édition multi-calques (images, texte, formes, actifs)"),
        useInlineTranslation("Visual font library (15+ professional fonts)", "Bibliothèque de polices visuelles (15+ polices pro)"),
        useInlineTranslation("Custom asset upload and management", "Upload et gestion d'actifs personnalisés"),
        useInlineTranslation("Background removal and replacement", "Suppression et remplacement d'arrière-plan"),
        useInlineTranslation("Text templates with preset styles", "Modèles de texte avec styles prédéfinis"),
        useInlineTranslation("4K export resolution", "Résolution d'export 4K"),
        useInlineTranslation("A/B testing thumbnail variations", "Tests A/B de variations de miniatures")
      ]
    },
    "AI Audio Generator": {
      name: useInlineTranslation("AI Audio Generator", "Générateur Audio IA"),
      description: useInlineTranslation("Generate high-quality narrations and melodies with advanced AI voice synthesis", "Générez des narrations et mélodies de haute qualité avec synthèse vocale IA avancée"),
      features: [
        useInlineTranslation("AI narration generation with multiple voice options", "Génération de narrations IA avec options vocales multiples"),
        useInlineTranslation("AI melody and background music creation", "Création de mélodies et musiques de fond IA"),
        useInlineTranslation("Audio editing and mixing tools", "Outils d'édition et mixage audio"),
        useInlineTranslation("Waveform visualization and editing", "Visualisation et édition de formes d'onde"),
        useInlineTranslation("Voice cloning capabilities", "Capacités de clonage vocal"),
        useInlineTranslation("Christian-themed background music library", "Bibliothèque de musiques de fond chrétiennes"),
        useInlineTranslation("Audio effects and filters", "Effets et filtres audio"),
        useInlineTranslation("Multiple format exports (MP3, WAV, AAC)", "Exports en formats multiples (MP3, WAV, AAC)"),
        useInlineTranslation("Commercial use licensing included", "Licence d'utilisation commerciale incluse")
      ]
    },
    "AI Sound Generator": {
      name: useInlineTranslation("AI Sound Generator", "Générateur de Sons IA"),
      description: useInlineTranslation("Create professional sound effects and ambient audio for your content", "Créez des effets sonores professionnels et audio ambiant pour votre contenu"),
      features: [
        useInlineTranslation("AI-generated sound effects for videos", "Effets sonores générés par IA pour vidéos"),
        useInlineTranslation("Ambient background audio creation", "Création d'audio ambiant de fond"),
        useInlineTranslation("Custom sound library for Christian content", "Bibliothèque sonore personnalisée pour contenu chrétien"),
        useInlineTranslation("Audio layering and mixing tools", "Outils de superposition et mixage audio"),
        useInlineTranslation("Real-time sound generation", "Génération sonore en temps réel"),
        useInlineTranslation("Multiple audio format support", "Support de formats audio multiples"),
        useInlineTranslation("Royalty-free sound creation", "Création sonore libre de droits"),
        useInlineTranslation("Sync with video timeline", "Synchronisation avec timeline vidéo"),
        useInlineTranslation("Professional audio quality", "Qualité audio professionnelle")
      ]
    },
    "AI Title & Description Generator": {
      name: useInlineTranslation("AI Title & Description Generator", "Générateur de Titres & Descriptions IA"),
      description: useInlineTranslation("Create engaging titles and SEO-optimized descriptions that drive more views", "Créez des titres captivants et descriptions optimisées SEO qui génèrent plus de vues"),
      features: [
        useInlineTranslation("AI-powered title generation with Christian context", "Génération de titres IA avec contexte chrétien"),
        useInlineTranslation("SEO-optimized descriptions for maximum reach", "Descriptions optimisées SEO pour portée maximale"),
        useInlineTranslation("Platform-specific formatting (YouTube, Instagram, Facebook, TikTok)", "Formatage spécifique aux plateformes (YouTube, Instagram, Facebook, TikTok)"),
        useInlineTranslation("Hashtag generation and optimization", "Génération et optimisation de hashtags"),
        useInlineTranslation("A/B testing title suggestions", "Suggestions de titres pour tests A/B"),
        useInlineTranslation("Engagement prediction scoring", "Score de prédiction d'engagement"),
        useInlineTranslation("Content calendar integration", "Intégration de calendrier de contenu"),
        useInlineTranslation("Trending topic incorporation", "Incorporation de sujets tendance"),
        useInlineTranslation("Bulk generation capabilities", "Capacités de génération en masse")
      ]
    },
    "AI Prompt Generator": {
      name: useInlineTranslation("AI Prompt Generator", "Générateur de Prompts IA"),
      description: useInlineTranslation("Never run out of content ideas with our intelligent Christian content assistant", "Ne manquez jamais d'idées de contenu avec notre assistant de contenu chrétien intelligent"),
      features: [
        useInlineTranslation("Interactive AI assistant for content ideation", "Assistant IA interactif pour idéation de contenu"),
        useInlineTranslation("Christian content specialization with Bible knowledge", "Spécialisation contenu chrétien avec connaissance biblique"),
        useInlineTranslation("Trending topic suggestions", "Suggestions de sujets tendance"),
        useInlineTranslation("Content calendar planning", "Planification de calendrier de contenu"),
        useInlineTranslation("Scripture-based content prompts", "Prompts de contenu basés sur les Écritures"),
        useInlineTranslation("Seasonal and holiday content ideas", "Idées de contenu saisonnier et jours fériés"),
        useInlineTranslation("Audience engagement optimization", "Optimisation de l'engagement de l'audience"),
        useInlineTranslation("Cross-platform content adaptation", "Adaptation de contenu multi-plateformes"),
        useInlineTranslation("Content series planning", "Planification de séries de contenu")
      ]
    },
    "AI Image Analysis & Description": {
      name: useInlineTranslation("AI Image Analysis & Description", "Analyse & Description d'Images IA"),
      description: useInlineTranslation("Understand and describe your images with advanced computer vision", "Comprenez et décrivez vos images avec vision par ordinateur avancée"),
      features: [
        useInlineTranslation("AI-powered image description using advanced vision models", "Description d'images IA avec modèles de vision avancés"),
        useInlineTranslation("Content tagging and categorization", "Étiquetage et catégorisation de contenu"),
        useInlineTranslation("Accessibility text generation (alt-text)", "Génération de texte d'accessibilité (texte alt)"),
        useInlineTranslation("Batch image processing", "Traitement d'images par lots"),
        useInlineTranslation("Object detection and identification", "Détection et identification d'objets"),
        useInlineTranslation("Scene analysis and context understanding", "Analyse de scène et compréhension du contexte"),
        useInlineTranslation("Text extraction from images (OCR)", "Extraction de texte depuis images (OCR)"),
        useInlineTranslation("Content moderation and appropriateness checking", "Modération de contenu et vérification d'appropriation")
      ]
    },
    "AI Content Idea Generator": {
      name: useInlineTranslation("AI Content Idea Generator", "Générateur d'Idées de Contenu IA"),
      description: useInlineTranslation("Generate endless Christian content ideas tailored to your audience", "Générez des idées infinies de contenu chrétien adaptées à votre audience"),
      features: [
        useInlineTranslation("Christian news content integration", "Intégration de contenu d'actualités chrétiennes"),
        useInlineTranslation("Bible verse suggestions with context", "Suggestions de versets bibliques avec contexte"),
        useInlineTranslation("Devotional content generation", "Génération de contenu dévotionnel"),
        useInlineTranslation("Prayer request and response ideas", "Idées de demandes et réponses de prière"),
        useInlineTranslation("Seasonal Christian content calendar", "Calendrier de contenu chrétien saisonnier"),
        useInlineTranslation("Scripture-based content themes", "Thèmes de contenu basés sur les Écritures"),
        useInlineTranslation("Community engagement prompts", "Prompts d'engagement communautaire"),
        useInlineTranslation("Cross-reference biblical connections", "Références croisées de connexions bibliques")
      ]
    },
    "Social Media Direct Sharing": {
      name: useInlineTranslation("Social Media Direct Sharing", "Partage Direct Réseaux Sociaux"),
      description: useInlineTranslation("Upload and share your content directly to social platforms without leaving our app (Creator Lite and above)", "Uploadez et partagez votre contenu directement sur les réseaux sociaux sans quitter notre app (Creator Lite et supérieur)"),
      features: [
        useInlineTranslation("Direct YouTube upload without YouTube Studio", "Upload YouTube direct sans YouTube Studio"),
        useInlineTranslation("Automatic video optimization for each platform", "Optimisation vidéo automatique pour chaque plateforme"),
        useInlineTranslation("Bulk upload and scheduling capabilities (Creator Pro+)", "Capacités d'upload en masse et planification (Creator Pro+)"),
        useInlineTranslation("Cross-platform posting (YouTube, Instagram, Facebook, TikTok)", "Publication multi-plateformes (YouTube, Instagram, Facebook, TikTok)"),
        useInlineTranslation("Auto-generated descriptions and tags per platform", "Descriptions et tags auto-générés par plateforme"),
        useInlineTranslation("Thumbnail optimization for each social network", "Optimisation de miniatures pour chaque réseau social"),
        useInlineTranslation("Publishing calendar and scheduling (Creator Pro+)", "Calendrier de publication et planification (Creator Pro+)"),
        useInlineTranslation("Performance analytics and insights", "Analytiques de performance et insights"),
        useInlineTranslation("One-click publishing workflow", "Flux de publication en un clic")
      ]
    },
    "Project Management & Collaboration Hub": {
      name: useInlineTranslation("Project Management & Collaboration Hub", "Hub de Gestion de Projet & Collaboration"),
      description: useInlineTranslation("Collaborate seamlessly with your team on all content projects", "Collaborez facilement avec votre équipe sur tous vos projets de contenu"),
      features: [
        useInlineTranslation("Multi-user project sharing", "Partage de projets multi-utilisateurs"),
        useInlineTranslation("Version control and history", "Contrôle de version et historique"),
        useInlineTranslation("Comment and approval workflows", "Flux de travail de commentaires et d'approbation"),
        useInlineTranslation("Team member role management", "Gestion des rôles des membres d'équipe"),
        useInlineTranslation("Progress tracking and deadlines", "Suivi des progrès et échéances"),
        useInlineTranslation("Asset library sharing", "Partage de bibliothèque d'actifs"),
        useInlineTranslation("Workflow automation", "Automatisation du flux de travail"),
        useInlineTranslation("Analytics and reporting dashboard", "Tableau de bord d'analytiques et rapports")
      ]
    }
  };

  // Platform advantages translations
  const advantageTranslations: Record<string, { title: string, description: string, benefits: string[] }> = {
    "Direct Social Media Publishing": {
      title: useInlineTranslation("Direct Social Media Publishing", "Publication Direct Réseaux Sociaux"),
      description: useInlineTranslation("Upload directly to YouTube and other platforms without switching apps", "Uploadez directement sur YouTube et autres plateformes sans changer d'application"),
      benefits: [
        useInlineTranslation("Skip YouTube Studio - upload directly from our platform", "Évitez YouTube Studio - uploadez directement depuis notre plateforme"),
        useInlineTranslation("Auto-optimized descriptions and tags for each platform", "Descriptions et tags auto-optimisés pour chaque plateforme"),
        useInlineTranslation("Bulk upload and scheduling capabilities", "Capacités d'upload en masse et de planification"),
        useInlineTranslation("Cross-platform publishing in one click", "Publication multi-plateformes en un clic"),
        useInlineTranslation("Built-in analytics and performance tracking", "Analytiques et suivi de performance intégrés")
      ]
    },
    "All-in-One Platform": {
      title: useInlineTranslation("All-in-One Platform", "Plateforme Tout-en-Un"),
      description: useInlineTranslation("Create, edit, and share all your Christian content from one powerful platform", "Créez, modifiez et partagez tout votre contenu chrétien depuis une plateforme puissante"),
      benefits: [
        useInlineTranslation("No more jumping between 5-8 different tools", "Fini de jongler entre 5-8 outils différents"),
        useInlineTranslation("Seamless workflow from ideation to publication", "Flux de travail fluide de l'idéation à la publication"),
        useInlineTranslation("Integrated asset management across all projects", "Gestion d'actifs intégrée sur tous les projets"),
        useInlineTranslation("Unified brand consistency across all content", "Cohérence de marque unifiée sur tout le contenu")
      ]
    },
    "Best AI Models at Your Fingertips": {
      title: useInlineTranslation("Best AI Models at Your Fingertips", "Meilleurs Modèles IA à Portée de Main"),
      description: useInlineTranslation("Access the most advanced AI models without managing multiple subscriptions", "Accédez aux modèles IA les plus avancés sans gérer plusieurs abonnements"),
      benefits: [
        useInlineTranslation("OpenAI GPT & Whisper for text and transcription", "OpenAI GPT & Whisper pour texte et transcription"),
        useInlineTranslation("Claude Sonnet for creative content generation", "Claude Sonnet pour génération de contenu créatif"),
        useInlineTranslation("Google Gemini for advanced vision tasks", "Google Gemini pour tâches de vision avancées"),
        useInlineTranslation("SDXL for professional image generation", "SDXL pour génération d'images professionnelles"),
        useInlineTranslation("Custom models trained specifically for Christian content", "Modèles personnalisés formés spécifiquement pour contenu chrétien")
      ]
    },
    "Christian-Focused AI Training": {
      title: useInlineTranslation("Christian-Focused AI Training", "Formation IA Axée Chrétienne"),
      description: useInlineTranslation("AI models specially trained to understand Christian context and values", "Modèles IA spécialement formés pour comprendre le contexte et les valeurs chrétiennes"),
      benefits: [
        useInlineTranslation("Biblical terminology and theological accuracy", "Terminologie biblique et précision théologique"),
        useInlineTranslation("Appropriate tone for different Christian audiences", "Ton approprié pour différentes audiences chrétiennes"),
        useInlineTranslation("Cultural sensitivity and doctrinal awareness", "Sensibilité culturelle et conscience doctrinale"),
        useInlineTranslation("Scripture-based content suggestions", "Suggestions de contenu basées sur les Écritures"),
        useInlineTranslation("Ministry-appropriate language and themes", "Langage et thèmes appropriés au ministère")
      ]
    },
    "Complete Workflow Integration": {
      title: useInlineTranslation("Complete Workflow Integration", "Intégration Complète du Flux de Travail"),
      description: useInlineTranslation("From creation to publication - never leave our platform", "De la création à la publication - ne quittez jamais notre plateforme"),
      benefits: [
        useInlineTranslation("Direct publishing to YouTube without YouTube Studio", "Publication directe sur YouTube sans YouTube Studio"),
        useInlineTranslation("Instagram, Facebook, and TikTok integration", "Intégration Instagram, Facebook et TikTok"),
        useInlineTranslation("Platform-specific optimization and formatting", "Optimisation et formatage spécifiques à chaque plateforme"),
        useInlineTranslation("Scheduled posting and content calendar", "Publication planifiée et calendrier de contenu"),
        useInlineTranslation("Unified analytics across all platforms", "Analytiques unifiées sur toutes les plateformes")
      ]
    },
    "Massive Cost Savings": {
      title: useInlineTranslation("Massive Cost Savings", "Économies Massives"),
      description: useInlineTranslation("Save hundreds of dollars monthly vs buying tools separately", "Économisez des centaines de dollars par mois vs acheter les outils séparément"),
      benefits: [
        useInlineTranslation("Replace 8+ expensive tools with one platform", "Remplacez 8+ outils coûteux par une seule plateforme"),
        useInlineTranslation("No more $150-200/month in subscriptions", "Fini les 150-200$/mois d'abonnements"),
        useInlineTranslation("Fair use pricing with credit add-ons", "Tarification équitable avec crédits supplémentaires"),
        useInlineTranslation("Scale up or down based on your needs", "Augmentez ou diminuez selon vos besoins"),
        useInlineTranslation("Transparent pricing with no hidden fees", "Tarification transparente sans frais cachés")
      ]
    },
    "Team Collaboration Built-In": {
      title: useInlineTranslation("Team Collaboration Built-In", "Collaboration d'Équipe Intégrée"),
      description: useInlineTranslation("Work together seamlessly with your ministry team", "Travaillez ensemble facilement avec votre équipe de ministère"),
      benefits: [
        useInlineTranslation("Multi-user access and permissions", "Accès multi-utilisateurs et permissions"),
        useInlineTranslation("Comment and approval workflows", "Flux de travail de commentaires et d'approbation"),
        useInlineTranslation("Shared asset libraries and templates", "Bibliothèques d'actifs et modèles partagés"),
        useInlineTranslation("Progress tracking and project management", "Suivi des progrès et gestion de projet"),
        useInlineTranslation("Role-based access control", "Contrôle d'accès basé sur les rôles")
      ]
    }
  };

  // Competitor comparison translations
  const comparisonTranslations = [
    {
      tool: useInlineTranslation("Jasper AI (Scripts)", "Jasper AI (Scripts)"),
      price: useInlineTranslation("$49/month", "49$/mois"),
      limitation: useInlineTranslation("Generic scripts, no Christian context or video integration", "Scripts génériques, pas de contexte chrétien ni d'intégration vidéo")
    },
    {
      tool: useInlineTranslation("Canva Pro", "Canva Pro"),
      price: useInlineTranslation("$15/month", "15$/mois"),
      limitation: useInlineTranslation("Design only, no video or AI audio", "Design uniquement, pas de vidéo ni d'audio IA")
    },
    {
      tool: useInlineTranslation("Descript", "Descript"),
      price: useInlineTranslation("$15/month", "15$/mois"),
      limitation: useInlineTranslation("Video editing only, no thumbnails or content generation", "Montage vidéo uniquement, pas de miniatures ni de génération de contenu")
    },
    {
      tool: useInlineTranslation("ElevenLabs", "ElevenLabs"),
      price: useInlineTranslation("$22/month", "22$/mois"),
      limitation: useInlineTranslation("Audio only, no video or design tools", "Audio uniquement, pas d'outils vidéo ou de design")
    },
    {
      tool: useInlineTranslation("Rev.com", "Rev.com"),
      price: useInlineTranslation("$25/hour", "25$/heure"),
      limitation: useInlineTranslation("Transcription only, expensive per-hour pricing", "Transcription uniquement, tarification à l'heure coûteuse")
    },
    {
      tool: useInlineTranslation("Scriptwriting Services", "Services de Rédaction de Scripts"),
      price: useInlineTranslation("$50-100/project", "50-100$/projet"),
      limitation: useInlineTranslation("Expensive per-project, no Christian focus", "Coûteux par projet, pas d'orientation chrétienne")
    },
    {
      tool: useInlineTranslation("Adobe Creative", "Adobe Creative"),
      price: useInlineTranslation("$60/month", "60$/mois"),
      limitation: useInlineTranslation("No AI generation, steep learning curve", "Pas de génération IA, courbe d'apprentissage abrupte")
    },
    {
      tool: useInlineTranslation("Multiple Tools Total", "Total Plusieurs Outils"),
      price: useInlineTranslation("$200-250/month", "200-250$/mois"),
      limitation: useInlineTranslation("Fragmented workflow, multiple logins, no integration", "Flux de travail fragmenté, connexions multiples, aucune intégration")
    }
  ];

  // Comparison section text (must be at top level)
  const compSectionHeader = useInlineTranslation("The All-in-One Advantage", "L'Avantage Tout-en-Un");
  const compSectionSubheader = useInlineTranslation("See how one integrated platform compares to managing multiple disconnected tools", "Voyez comment une plateforme intégrée se compare à la gestion de plusieurs outils déconnectés");
  const compCol1 = useInlineTranslation("Tool/Service", "Outil/Service");
  const compCol2 = useInlineTranslation("Monthly Cost", "Coût Mensuel");
  const compCol3 = useInlineTranslation("Limitations", "Limitations");
  const compTotal1 = useInlineTranslation("Managing Multiple Tools:", "Gestion de Plusieurs Outils:");
  const compTotal1Price = useInlineTranslation("$220-270/month + hours switching contexts", "220-270$/mois + heures perdues à changer d'outils");
  const compTotal2 = useInlineTranslation("One Complete Platform:", "Une Plateforme Complète:");
  const compTotal2Price = useInlineTranslation("From $24.99/month (Unified workflow)", "À partir de 24,99$/mois (Flux de travail unifié)");
  const compSavings = useInlineTranslation("💰 Save money, time, and mental energy — focus on creating, not tool management", "💰 Économisez argent, temps et énergie mentale — concentrez-vous sur la création, pas la gestion d'outils");

  // Apps section text
  const appsHeader = useInlineTranslation("Your Complete Video Production Toolkit", "Votre Boîte à Outils Complète de Production Vidéo");
  const appsSubheader = useInlineTranslation("Everything you need to create professional content — script generation, visual creation, audio production, editing, and publishing", "Tout ce dont vous avez besoin pour créer du contenu professionnel — génération de scripts, création visuelle, production audio, montage et publication");
  const aiModelsUsed = useInlineTranslation("🧠 AI Models Used:", "🧠 Modèles IA Utilisés:");
  const keyFeatures = useInlineTranslation("✨ Key Features:", "✨ Fonctionnalités Clés:");

  // Advantages section text
  const advHeader = useInlineTranslation("Why Creators Choose Our Platform", "Pourquoi les Créateurs Choisissent Notre Plateforme");
  const advSubheader = useInlineTranslation("Stop context-switching between tools. Work faster, create more, and maintain consistency with one unified workspace.", "Arrêtez de jongler entre les outils. Travaillez plus vite, créez plus et maintenez la cohérence avec un seul espace de travail unifié.");

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.features.title}
        description={seoConfig.features.description}
        keywords={seoConfig.features.keywords}
        url="https://kingdomstudio.pro/features"
      />
      <Navigation />
      <div className={styles.container}>
        {/* Value Hero Section */}
        <div className={styles.hero}>
          <h1 className={styles.title}>{useInlineTranslation("From Script to Professional Video in 30 Minutes", "Du Script à la Vidéo Professionnelle en 30 Minutes")}</h1>
          <p className={styles.subtitle}>
            {useInlineTranslation("One complete AI platform that empowers creators to produce marketing videos, social content, and mini videos at scale — without juggling multiple tools", "Une plateforme IA complète qui permet aux créateurs de produire des vidéos marketing, du contenu social et des mini-vidéos à grande échelle — sans jongler entre plusieurs outils")}
          </p>
          <div className={styles.heroStats}>
            <div className={styles.stat}>
              <span className={styles.statIcon}>🎬</span>
              <span className={styles.statNumber}>{useInlineTranslation("Complete Workflow", "Flux Complet")}</span>
              <span className={styles.statLabel}>{useInlineTranslation("Script → Edit → Publish", "Script → Montage → Publication")}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statIcon}>⚡</span>
              <span className={styles.statNumber}>{useInlineTranslation("30 Minutes", "30 Minutes")}</span>
              <span className={styles.statLabel}>{useInlineTranslation("Idea to Published Video", "Idée à Vidéo Publiée")}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statIcon}>🚀</span>
              <span className={styles.statNumber}>{useInlineTranslation("One Platform", "Une Plateforme")}</span>
              <span className={styles.statLabel}>{useInlineTranslation("Replace 8+ Tools", "Remplace 8+ Outils")}</span>
            </div>
          </div>
        </div>

        {/* Use Cases Showcase */}
        <div className={styles.useCasesSection}>
          <h2 className={styles.sectionTitle}>{useInlineTranslation("What Can You Create?", "Que Pouvez-Vous Créer?")}</h2>
          <div className={styles.useCasesGrid}>
            <div className={styles.useCaseCard}>
              <div className={styles.useCaseIcon}>📱</div>
              <h3>{useInlineTranslation("AI Marketing Videos", "Vidéos Marketing IA")}</h3>
              <p>{useInlineTranslation("Generate complete campaigns: scripts, voiceovers, visuals, and captions — all in one place", "Générez des campagnes complètes: scripts, voix off, visuels et sous-titres — tout en un seul endroit")}</p>
              <div className={styles.useCaseExample}>
                <strong>{useInlineTranslation("Example:", "Exemple:")}</strong> {useInlineTranslation("Launch a product with 5 social videos, thumbnails, and posts in one afternoon", "Lancez un produit avec 5 vidéos sociales, miniatures et publications en un après-midi")}
              </div>
            </div>
            <div className={styles.useCaseCard}>
              <div className={styles.useCaseIcon}>🎥</div>
              <h3>{useInlineTranslation("Mini Videos & Shorts", "Mini Vidéos & Shorts")}</h3>
              <p>{useInlineTranslation("Turn ideas into polished short-form content with AI assistance at every step", "Transformez des idées en contenu court et soigné avec l'assistance IA à chaque étape")}</p>
              <div className={styles.useCaseExample}>
                <strong>{useInlineTranslation("Example:", "Exemple:")}</strong> {useInlineTranslation("Create a week's worth of TikTok/Instagram Reels in under an hour", "Créez une semaine de TikTok/Instagram Reels en moins d'une heure")}
              </div>
            </div>
            <div className={styles.useCaseCard}>
              <div className={styles.useCaseIcon}>📊</div>
              <h3>{useInlineTranslation("Educational Content", "Contenu Éducatif")}</h3>
              <p>{useInlineTranslation("Produce training videos, tutorials, and explainers with professional polish", "Produisez des vidéos de formation, tutoriels et explications avec finition professionnelle")}</p>
              <div className={styles.useCaseExample}>
                <strong>{useInlineTranslation("Example:", "Exemple:")}</strong> {useInlineTranslation("Build a complete course with narrated lessons and custom graphics", "Créez un cours complet avec leçons narrées et graphiques personnalisés")}
              </div>
            </div>
            <div className={styles.useCaseCard}>
              <div className={styles.useCaseIcon}>💼</div>
              <h3>{useInlineTranslation("Brand Stories", "Histoires de Marque")}</h3>
              <p>{useInlineTranslation("Craft compelling narratives with AI-generated scripts, images, and soundtracks", "Créez des récits captivants avec scripts, images et musiques générés par IA")}</p>
              <div className={styles.useCaseExample}>
                <strong>{useInlineTranslation("Example:", "Exemple:")}</strong> {useInlineTranslation("Share your brand's journey with cinematic videos and thumbnails", "Partagez le parcours de votre marque avec vidéos cinématiques et miniatures")}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={styles.tabNavigation}>
          <button 
            className={`${styles.tabButton} ${activeSection === 'apps' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('apps')}
          >
            {useInlineTranslation("🚀 Complete Toolkit", "🚀 Boîte à Outils Complète")}
          </button>
          <button 
            className={`${styles.tabButton} ${activeSection === 'advantages' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('advantages')}
          >
            {useInlineTranslation("⚡ Why One Platform", "⚡ Pourquoi Une Plateforme")}
          </button>
          <button 
            className={`${styles.tabButton} ${activeSection === 'comparison' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('comparison')}
          >
            {useInlineTranslation("📊 Value Comparison", "📊 Comparaison de Valeur")}
          </button>
        </div>

        {/* Apps & Features Section */}
        {activeSection === 'apps' && (
          <div className={styles.appsSection}>
            <div className={styles.sectionHeader}>
              <h2>{appsHeader}</h2>
              <p>{appsSubheader}</p>
            </div>
            
            <div className={styles.appsGrid}>
              {appFeatures.map((app, index) => (
                <div 
                  key={index} 
                  className={`${styles.appCard} ${selectedApp === index ? styles.expandedCard : ''}`}
                  onClick={() => setSelectedApp(selectedApp === index ? null : index)}
                >
                  <div className={styles.appHeader}>
                    <span className={styles.appIcon}>{app.icon}</span>
                    <div>
                      <h3 className={styles.appName}>{appTranslations[app.name]?.name || app.name}</h3>
                      <p className={styles.appDescription}>{appTranslations[app.name]?.description || app.description}</p>
                    </div>
                    <button className={styles.expandButton}>
                      {selectedApp === index ? '−' : '+'}
                    </button>
                  </div>
                  
                  {selectedApp === index && (
                    <div className={styles.appDetails}>
                      <div className={styles.modelsUsed}>
                        {/* <h4>{aiModelsUsed}</h4> */}
                        {/* <div className={styles.modelTags}>
                          {app.models.map((model, idx) => (
                            <span key={idx} className={styles.modelTag}>{model}</span>
                          ))}
                        </div> */}
                      </div>
                      
                      <div className={styles.featuresList}>
                        <h4>{keyFeatures}</h4>
                        <ul>
                          {(appTranslations[app.name]?.features || app.features).map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Platform Advantages Section */}
        {activeSection === 'advantages' && (
          <div className={styles.advantagesSection}>
            <div className={styles.sectionHeader}>
              <h2>{advHeader}</h2>
              <p>{advSubheader}</p>
            </div>
            
            <div className={styles.advantagesGrid}>
              {platformAdvantages.map((advantage, index) => (
                <div key={index} className={styles.advantageCard}>
                  <div className={styles.advantageIcon}>{advantage.icon}</div>
                  <h3 className={styles.advantageTitle}>{advantageTranslations[advantage.title]?.title || advantage.title}</h3>
                  <p className={styles.advantageDescription}>{advantageTranslations[advantage.title]?.description || advantage.description}</p>
                  <ul className={styles.benefitsList}>
                    {advantageTranslations[advantage.title]?.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    )) || advantage.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Competitor Comparison Section */}
        {activeSection === 'comparison' && (
          <div className={styles.comparisonSection}>
            <div className={styles.sectionHeader}>
              <h2>{compSectionHeader}</h2>
              <p>{compSectionSubheader}</p>
            </div>
            
            <div className={styles.comparisonTable}>
              <div className={styles.comparisonHeader}>
                <div className={styles.comparisonColumn}>{compCol1}</div>
                <div className={styles.comparisonColumn}>{compCol2}</div>
                <div className={styles.comparisonColumn}>{compCol3}</div>
              </div>
              
              {comparisonTranslations.map((competitor, index) => (
                <div key={index} className={styles.comparisonRow}>
                  <div className={styles.comparisonCell}>{competitor.tool}</div>
                  <div className={styles.comparisonCell}>{competitor.price}</div>
                  <div className={styles.comparisonCell}>{competitor.limitation}</div>
                </div>
              ))}
              
              <div className={styles.comparisonFooter}>
                <div className={styles.totalCostCard}>
                  <h3>{compTotal1} <span className={styles.expensivePrice}>{compTotal1Price}</span></h3>
                  <h3>{compTotal2} <span className={styles.ourPrice}>{compTotal2Price}</span></h3>
                  <div className={styles.savings}>
                    <strong>{compSavings}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className={styles.ctaSection}>
          <h2>{useInlineTranslation("Ready to Create Professional Videos in Minutes?", "Prêt à Créer des Vidéos Professionnelles en Minutes?")}</h2>
          <p>{useInlineTranslation("Join creators who've streamlined their workflow from hours to minutes", "Joignez les créateurs qui ont optimisé leur flux de travail de plusieurs heures à quelques minutes")}</p>
          <div className={styles.ctaButtons}>
            <button 
              className={styles.primaryButton}
              onClick={() => navigate('/create-content')}
            >
              {useInlineTranslation("Start Creating Free", "Commencer Gratuitement")}
            </button>
            <button 
              className={styles.secondaryButton}
              onClick={() => navigate('/pricing')}
            >
              {useInlineTranslation("View Plans & Features", "Voir Plans & Fonctionnalités")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}